import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { User, Phone, Lock, ArrowLeft, UserPlus, Shield, Briefcase, Eye, Wallet, MapPinned } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", phone: "", password: "", role: "petugas" });
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.phone.trim() || !form.password.trim()) {
      toast.error("Isi semua field");
      return;
    }
    if (form.password.length < 4) {
      toast.error("Password minimal 4 karakter");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/register`, {
        username: form.username.trim(),
        phone: form.phone.trim(),
        password: form.password,
        role: form.role
      });
      toast.success("Registrasi berhasil! Menunggu persetujuan admin.");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registrasi gagal");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex flex-col">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Beranda
            </Button>
          </Link>
        </nav>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="glass-card-solid">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-2xl font-heading font-bold text-slate-900">Daftar Akun</h1>
                <p className="text-slate-500 text-sm mt-1">Daftar sebagai petugas, agen, pengawas, kasir, atau petugas lapangan</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nama Lengkap</label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Masukkan nama"
                      value={form.username}
                      onChange={(e) => setForm({...form, username: e.target.value})}
                      className="h-12 pl-11 rounded-xl"
                      data-testid="register-name-input"
                    />
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">No. HP</label>
                  <div className="relative">
                    <Input
                      type="tel"
                      placeholder="08xxxxxxxxxx"
                      value={form.phone}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      className="h-12 pl-11 rounded-xl"
                      data-testid="register-phone-input"
                    />
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
                  <div className="relative">
                    <Input
                      type="password"
                      placeholder="Minimal 4 karakter"
                      value={form.password}
                      onChange={(e) => setForm({...form, password: e.target.value})}
                      className="h-12 pl-11 rounded-xl"
                      data-testid="register-password-input"
                    />
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
                  </div>
                </div>

                {/* Role Selection */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Pilih Peran</label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({...form, role: "petugas"})}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        form.role === "petugas"
                          ? "border-sky-500 bg-sky-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-sky-300"
                      }`}
                      data-testid="role-petugas-btn"
                    >
                      <Shield className={`w-5 h-5 mx-auto mb-1 ${form.role === "petugas" ? "text-sky-600" : "text-slate-400"}`} />
                      <p className={`font-semibold text-xs ${form.role === "petugas" ? "text-sky-600" : "text-slate-700"}`}>Petugas</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Khusus PDAM</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({...form, role: "agen"})}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        form.role === "agen"
                          ? "border-emerald-500 bg-emerald-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-emerald-300"
                      }`}
                      data-testid="role-agen-btn"
                    >
                      <Briefcase className={`w-5 h-5 mx-auto mb-1 ${form.role === "agen" ? "text-emerald-600" : "text-slate-400"}`} />
                      <p className={`font-semibold text-xs ${form.role === "agen" ? "text-emerald-600" : "text-slate-700"}`}>Agen</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Semua layanan</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({...form, role: "pengawas"})}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        form.role === "pengawas"
                          ? "border-purple-500 bg-purple-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-purple-300"
                      }`}
                      data-testid="role-pengawas-btn"
                    >
                      <Eye className={`w-5 h-5 mx-auto mb-1 ${form.role === "pengawas" ? "text-purple-600" : "text-slate-400"}`} />
                      <p className={`font-semibold text-xs ${form.role === "pengawas" ? "text-purple-600" : "text-slate-700"}`}>Pengawas</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Lihat laporan</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({...form, role: "kasir"})}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        form.role === "kasir"
                          ? "border-amber-500 bg-amber-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-amber-300"
                      }`}
                      data-testid="role-kasir-btn"
                    >
                      <Wallet className={`w-5 h-5 mx-auto mb-1 ${form.role === "kasir" ? "text-amber-600" : "text-slate-400"}`} />
                      <p className={`font-semibold text-xs ${form.role === "kasir" ? "text-amber-600" : "text-slate-700"}`}>Kasir</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Tanpa biaya admin</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({...form, role: "petugas_lapangan"})}
                      className={`col-span-2 p-3 rounded-xl border-2 transition-all text-center ${
                        form.role === "petugas_lapangan"
                          ? "border-rose-500 bg-rose-50 shadow-sm"
                          : "border-slate-200 bg-white hover:border-rose-300"
                      }`}
                      data-testid="role-petugas-lapangan-btn"
                    >
                      <MapPinned className={`w-5 h-5 mx-auto mb-1 ${form.role === "petugas_lapangan" ? "text-rose-600" : "text-slate-400"}`} />
                      <p className={`font-semibold text-xs ${form.role === "petugas_lapangan" ? "text-rose-600" : "text-slate-700"}`}>Petugas Lapangan</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Read-only: cek tagihan & Peta Global</p>
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 rounded-full text-base font-semibold bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg hover:shadow-xl"
                  data-testid="register-submit-btn"
                >
                  {isLoading ? <div className="spinner w-5 h-5" /> : "Daftar"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500">
                  Sudah punya akun?{" "}
                  <Link to="/login" className="text-sky-600 font-medium hover:underline">
                    Masuk di sini
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
